import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { subscriptionAPI, billingAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const SubscriptionContext = createContext(null);

export const SubscriptionProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [features, setFeatures] = useState({});
  const [billingStatus, setBillingStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Platform admins bypass all subscription restrictions
  const isPlatformAdmin = user?.is_platform_admin === true;

  const loadSubscription = useCallback(async () => {
    if (!isAuthenticated) {
      setSubscription(null);
      setFeatures({});
      setBillingStatus(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [subRes, featRes, billRes] = await Promise.all([
        subscriptionAPI.get(),
        subscriptionAPI.getFeatures(),
        billingAPI.getStatus(),
      ]);
      setSubscription(subRes.data);
      setFeatures(featRes.data?.features || {});
      setBillingStatus(billRes.data);
    } catch {
      setSubscription(null);
      setFeatures({});
      setBillingStatus(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  const hasFeature = (feature) => {
    if (isPlatformAdmin) return true;
    const val = features[feature];
    if (typeof val === 'boolean') return val;
    if (typeof val === 'number') return val !== 0;
    return !!val;
  };

  const checkLimit = (limitType, currentCount) => {
    if (isPlatformAdmin) return true;
    const limit = features[limitType];
    if (limit === -1) return true;
    if (limit === undefined) return true;
    return currentCount < limit;
  };

  // Platform admins are never in read-only mode
  const isReadOnly = isPlatformAdmin ? false : (
    subscription?.status === 'expired' ||
    subscription?.status === 'cancelled' ||
    subscription?.status === 'past_due' ||
    (subscription?.status === 'trialing' && subscription?.trial_ends_at &&
      new Date(subscription.trial_ends_at) < new Date())
  );

  return (
    <SubscriptionContext.Provider value={{
      subscription, features, billingStatus, loading,
      hasFeature, checkLimit, refresh: loadSubscription,
      isReadOnly, isPlatformAdmin,
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) return {
    subscription: null, features: {}, billingStatus: null, loading: true,
    hasFeature: () => true, checkLimit: () => true, refresh: () => {},
    isReadOnly: false, isPlatformAdmin: false,
  };
  return ctx;
};
